// Form rules for creating a trading pair.
// Mirrors CreatePairInputDto and CreatePairTradingConfigDto in the assets
// service. The schema is published at <API base>/assets/api-docs-json, but
// several limits below (decimal places, ranges) are enforced by the backend
// without appearing in that document.

// The number rules are shared with the market and network forms.
import { asTrimmedString, compareDecimals, decimalProblem, integerProblem } from "config/decimals";

export const ORDER_TYPES = ["LIMIT", "MARKET", "STOP_LIMIT", "STOP_MARKET"];
export const TIME_IN_FORCES = ["GTC", "IOC", "FOK"];
export const TRADING_STATUSES = ["SCHEDULED", "TRADING", "CANCEL_ONLY", "HALTED", "DELISTED"];
export const REFERENCE_PRICE_SOURCES = ["LAST_TRADE", "MARK_PRICE", "INDEX_PRICE"];

// Form state keeps every typed value as a string. buildCreatePairBody
// converts each one to the type the API expects.
export const DEFAULT_TRADING_CONFIG = {
  minTradeAmount: "0.0001",
  maxTradeAmount: "100000",
  minNotional: "5",
  maxNotional: "1000000",
  pricePrecision: "8",
  priceTickSize: "0.01",
  quantityPrecision: "8",
  quantityStepSize: "0.00000001",
  allowedOrderTypes: ["LIMIT"],
  allowedTimeInForces: ["GTC"],
  tradingStatus: "SCHEDULED",
  feeScheduleId: "spot-standard",
  maxPriceDeviationBps: "1000",
  maxSlippageBps: "500",
  referencePriceSource: "LAST_TRADE",
  tradingStartsAt: "",
};

// kind decides validation and the payload type:
//   decimal  -> sent as a string, e.g. "0.01"
//   integer  -> sent as a number, e.g. 8 (checked against min and max)
//   text     -> sent as a string
//   datetime -> entered in local time, sent as an ISO string in UTC
export const TEXT_FIELDS = {
  minTradeAmount: { label: "Min Trade Amount", placeholder: "e.g. 0.0001", kind: "decimal" },
  maxTradeAmount: { label: "Max Trade Amount", placeholder: "e.g. 100000", kind: "decimal" },
  minNotional: { label: "Min Notional", placeholder: "e.g. 5", kind: "decimal" },
  maxNotional: { label: "Max Notional", placeholder: "e.g. 1000000", kind: "decimal" },
  pricePrecision: { label: "Price Precision", placeholder: "e.g. 8", kind: "integer", min: 0, max: 18, hint: "Decimal places allowed in a price, 0 to 18." },
  priceTickSize: { label: "Price Tick Size", placeholder: "e.g. 0.01", kind: "decimal", hint: "Smallest allowed price step." },
  quantityPrecision: { label: "Quantity Precision", placeholder: "e.g. 8", kind: "integer", min: 0, max: 18, hint: "Decimal places allowed in a quantity, 0 to 18." },
  quantityStepSize: { label: "Quantity Step Size", placeholder: "e.g. 0.00000001", kind: "decimal", hint: "Smallest allowed quantity step." },
  maxPriceDeviationBps: { label: "Max Price Deviation (bps)", placeholder: "e.g. 1000", kind: "integer", min: 0, max: 10000, hint: "0 to 10000. 100 bps = 1%." },
  maxSlippageBps: { label: "Max Slippage (bps)", placeholder: "e.g. 500", kind: "integer", min: 0, max: 10000, hint: "0 to 10000. 100 bps = 1%." },
  feeScheduleId: { label: "Fee Schedule ID", placeholder: "e.g. spot-standard", kind: "text", optional: true, hint: "Optional. Leave blank to omit it." },
  tradingStartsAt: { label: "Trading Starts At", kind: "datetime", hint: "Your local time. Sent to the server in UTC." },
};

const pickAllowed = (allowed, selected) => allowed.filter((value) => selected.includes(value));

export function validatePairForm({ baseMarket, quoteMarket, tradingConfig }) {
  const errors = {};

  if (!baseMarket) errors.baseMarketId = "Select a base market";
  if (!quoteMarket) {
    errors.quoteMarketId = "Select a quote market";
  } else if (baseMarket && baseMarket.id === quoteMarket.id) {
    errors.quoteMarketId = "Quote market must be different from the base market";
  }

  Object.entries(TEXT_FIELDS).forEach(([key, field]) => {
    const value = asTrimmedString(tradingConfig[key]);
    if (!value) {
      if (!field.optional) errors[key] = `${field.label} is required`;
      return;
    }

    let problem = null;
    if (field.kind === "decimal") {
      problem = decimalProblem(value, field.label);
    } else if (field.kind === "integer") {
      problem = integerProblem(value, field);
    } else if (field.kind === "datetime" && Number.isNaN(new Date(value).getTime())) {
      problem = `${field.label} is not a valid date and time`;
    }
    if (problem) errors[key] = problem;
  });

  const requireMinNotAboveMax = (minKey, maxKey) => {
    if (errors[minKey] || errors[maxKey]) return;
    const min = asTrimmedString(tradingConfig[minKey]);
    const max = asTrimmedString(tradingConfig[maxKey]);
    if (compareDecimals(min, max) > 0) {
      errors[maxKey] = `${TEXT_FIELDS[maxKey].label} must be at least the ${TEXT_FIELDS[minKey].label}`;
    }
  };
  requireMinNotAboveMax("minTradeAmount", "maxTradeAmount");
  requireMinNotAboveMax("minNotional", "maxNotional");

  if (!pickAllowed(ORDER_TYPES, tradingConfig.allowedOrderTypes).length) {
    errors.allowedOrderTypes = "Select at least one order type";
  }
  if (!pickAllowed(TIME_IN_FORCES, tradingConfig.allowedTimeInForces).length) {
    errors.allowedTimeInForces = "Select at least one time in force";
  }
  if (!TRADING_STATUSES.includes(tradingConfig.tradingStatus)) errors.tradingStatus = "Select a trading status";
  if (!REFERENCE_PRICE_SOURCES.includes(tradingConfig.referencePriceSource)) {
    errors.referencePriceSource = "Select a reference price source";
  }

  return errors;
}

// Call only after validatePairForm returns no errors.
export function buildCreatePairBody({ baseMarket, quoteMarket, isActive, tradingConfig: c }) {
  const feeScheduleId = asTrimmedString(c.feeScheduleId);

  return {
    baseMarketId: Number(baseMarket.id),
    quoteMarketId: Number(quoteMarket.id),
    isActive,
    tradingConfig: {
      minTradeAmount: asTrimmedString(c.minTradeAmount),
      maxTradeAmount: asTrimmedString(c.maxTradeAmount),
      minNotional: asTrimmedString(c.minNotional),
      maxNotional: asTrimmedString(c.maxNotional),
      pricePrecision: parseInt(c.pricePrecision, 10),
      priceTickSize: asTrimmedString(c.priceTickSize),
      quantityPrecision: parseInt(c.quantityPrecision, 10),
      quantityStepSize: asTrimmedString(c.quantityStepSize),
      // Unique values in the API's enum order, whatever order chips were clicked.
      allowedOrderTypes: pickAllowed(ORDER_TYPES, c.allowedOrderTypes),
      allowedTimeInForces: pickAllowed(TIME_IN_FORCES, c.allowedTimeInForces),
      tradingStatus: c.tradingStatus,
      // The API rejects an empty feeScheduleId, so leave the key out when blank.
      ...(feeScheduleId ? { feeScheduleId } : {}),
      maxPriceDeviationBps: parseInt(c.maxPriceDeviationBps, 10),
      maxSlippageBps: parseInt(c.maxSlippageBps, 10),
      referencePriceSource: c.referencePriceSource,
      tradingStartsAt: new Date(c.tradingStartsAt).toISOString(),
    },
  };
}

// Shared by every page that talks to the assets service.
export { getApiErrorMessages } from "config/apiErrors";
